import {
  NOTIFICATION_TYPES,
  DRAWER_PLACEMENTS,
} from "../../../../../config/constants";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  InputRef,
  Popover,
  Select,
  Space,
  Tree,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, CloseOutlined, CheckOutlined } from "@ant-design/icons";
import MigrationRuleService from "../../../../../services/migrationrules/MigrationRuleService";
import { GetTablesRequest } from "../../../../../models/tables/GetTablesRequest";
import TablesService from "../../../../../services/tables/TablesService";
import { UpdateMigrationRule } from "../../../../../models/migrationrules/UpdateMigrationRule";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { CreateMigrationRule } from "../../../../../models/migrationrules/CreateMigrationRule";
import Datatable from "../../../../shared/components/Datatable";
import { useTranslation } from "react-i18next";
import NotificationsUtil from "../../../../shared/utils/NotificationsUtil";

const { Search } = Input;

enum RowAction {
  View = 1,
  Edit,
  Create,
}

const RuleValues = ({
  entities,
  selectedEntity,
  newRuleValueModalEnabled,
  setNewRuleValueModalEnabled,
  selectable,
  multiSelect,
  selectedRows,
  setSelectedRows,
}: {
  entities: any;
  selectedEntity: any;
  newRuleValueModalEnabled: boolean;
  setNewRuleValueModalEnabled: (val: boolean) => void;
  selectable: boolean;
  multiSelect: boolean;
  selectedRows: any;
  setSelectedRows: any;
}) => {
  interface DataType {
    id: number;
    key: string;
    ruleId: string;
    ruleName: string;
    ruleDescription: string;
    joinCondition: string;
    valueColumn: string;
    defaultRule: boolean;
    predecessorEntityId: number | null;
  }

  type DataIndex = keyof DataType;

  const { t, i18n } = useTranslation("translations");

  const [data, setData] = useState<DataType[]>([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const searchInput = useRef<InputRef>(null);

  const [ruleIdSearchValue, setRuleIdSearchValue] = useState<string>("");
  const [ruleIdCheckedKeys, setRuleIdCheckedKeys] = useState<any>([]);
  const [ruleIdAscending, setRuleIdAscending] = useState(false);
  const [ruleIdDescending, setRuleIdDescending] = useState(false);

  const [ruleNameSearchValue, setRuleNameSearchValue] = useState<string>("");
  const [ruleNameFilters, setRuleNameFilters] = useState<any>([]);
  const [ruleNameCheckedKeys, setRuleNameCheckedKeys] = useState<any>([]);
  const [ruleNameAscending, setRuleNameAscending] = useState(false);
  const [ruleNameDescending, setRuleNameDescending] = useState(false);

  const [ruleDescriptionSearchValue, setRuleDescriptionSearchValue] =
    useState<string>("");
  const [ruleDescriptionFilters, setRuleDescriptionFilters] = useState<any>([]);
  const [ruleDescriptionCheckedKeys, setRuleDescriptionCheckedKeys] =
    useState<any>([]);
  const [ruleDescriptionAscending, setRuleDescriptionAscending] =
    useState(false);
  const [ruleDescriptionDescending, setRuleDescriptionDescending] =
    useState(false);

  const [joinSearchValue, setJoinSearchValue] = useState<string>("");
  const [joinFilters, setJoinFilters] = useState<any>([]);
  const [joinCheckedKeys, setJoinCheckedKeys] = useState<any>([]);
  const [joinAscending, setJoinAscending] = useState(false);
  const [joinDescending, setJoinDescending] = useState(false);

  const [valueSearchValue, setValueSearchValue] = useState<string>("");
  const [valueFilters, setValueFilters] = useState<any>([]);
  const [valueCheckedKeys, setValueCheckedKeys] = useState<any>([]);
  const [valueAscending, setValueAscending] = useState(false);
  const [valueDescending, setValueDescending] = useState(false);

  const [defaultSearchValue, setDefaultSearchValue] = useState<string>("");
  const [defaultFilters, setDefaultFilters] = useState<any>([]);
  const [defaultCheckedKeys, setDefaultCheckedKeys] = useState<any>([]);
  const [defaultAscending, setDefaultAscending] = useState(false);
  const [defaultDescending, setDefaultDescending] = useState(false);
  const [drawerAction, setDrawerAction] = useState<RowAction>();
  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editedRow, setEditedRow] = useState<any>();
  const [ruleNameInput, setRuleNameInput] = useState("");
  const [ruleDescriptionInput, setRuleDescriptionInput] = useState("");
  const [valueInput, setValueInput] = useState("");
  const [joinInput, setJoinInput] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [updateRowAllowed, setUpdateRowAllowed] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [destinationTable, setDestinationTable] = useState<any>();
  
  const [sortColumn, setSortColumn] = useState<any>(undefined);

  const [showJoinLengthValidationError, setShowJoinLengthValidationError] = useState<boolean>(false);

  const [selectedRulePredecessorEntity, setSelectedRulePredecessorEntity] =
    useState<any>();

  const showDrawer = (action: RowAction) => {
    setDrawerAction(action);
    switch (action) {
      case RowAction.View:
        setDrawerTitle(
          t("Page.Operation.MigrationRules.Tabs.RuleValues.Labels.RowDetails", {
            lngs: i18n.languages,
            defaultValue: "Row details",
          })!
        );
        break;
      case RowAction.Create:
        setDrawerTitle(
          t("Page.Operation.MigrationRules.Tabs.RuleValues.Labels.NewRule", {
            lngs: i18n.languages,
            defaultValue: "New rule",
          })!
        );
        break;
      case RowAction.Edit:
        setDrawerTitle(
          t("Page.Operation.MigrationRules.Tabs.RuleValues.Labels.EditRule", {
            lngs: i18n.languages,
            defaultValue: "Edit rule",
          })!
        );
        break;
      default:
        break;
    }
    setOpenDrawer(true);
  };

  const onClose = () => {
    setOpenDrawer(false);
    setRuleNameInput("");
    setRuleDescriptionInput("");
    setJoinInput("");
    setValueInput("");
    setIsDefault(false);
    setEditedRow(null);
    setSelectedRulePredecessorEntity(null);
    setNewRuleValueModalEnabled(false);
  };

  const onSaveClose = () => {
    setOpenDrawer(false);
    setRuleNameInput("");
    setRuleDescriptionInput("");
    setJoinInput("");
    setValueInput("");
    setIsDefault(false);
    setEditedRow(null);
    setSelectedRulePredecessorEntity(null);
  };

  const onEditRule = (record: any) => {
    showDrawer(RowAction.Edit);
    setRuleNameInput(record.ruleName);
    setRuleDescriptionInput(record.ruleDescription);
    setJoinInput(record.joinCondition);
    setValueInput(record.valueColumn);
    setIsDefault(record.defaultRule);
    setSelectedRulePredecessorEntity(record.predecessorEntityId);
    setEditedRow(record);
  };

  const onCopyRule = (record: any) => {
    showDrawer(RowAction.Create);
    setRuleNameInput(record.ruleName);
    setRuleDescriptionInput(record.ruleDescription);
    setJoinInput(record.joinCondition);
    setValueInput(record.valueColumn);
    setIsDefault(record.defaultRule);
    setSelectedRulePredecessorEntity(record.predecessorEntityId);
  };

  const onDoubleClickRow = (record: any) => {
    showDrawer(RowAction.View);
    setRuleNameInput(record.ruleName);
    setRuleDescriptionInput(record.ruleDescription);
    setJoinInput(record.joinCondition);
    setValueInput(record.valueColumn);
    setIsDefault(record.defaultRule);
    setSelectedRulePredecessorEntity(record.predecessorEntityId);
    setEditedRow(record);
  };

  const onDeleteRule = (id: number) => {
    MigrationRuleService.remove(id)
      .then((response: any) => {
        setIsTableLoading(true);
        NotificationsUtil.openNotificationDialog({
          description: t(
            "Page.Operation.MigrationRules.Tabs.RuleValues.Notifications.Rule.DeleteSuccessDescription",
            {
              lngs: i18n.languages,
              defaultValue: "Rule deleted successfully.",
            }
          )!,
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
        setLoadingButton(false);
        MigrationRuleService.getAll(destinationTable.id).then(
          (response: any) => {
            setData(
              response.data.map((x: any) => ({
                ...x,
                key: x.id,
                defaultRule: x.defaultRule === 1 ? true : false,
                predecessorEntity: x.predecessorEntityId
                  ? entities.find((y: any) => y.id === x.predecessorEntityId)
                      ?.name
                  : "",
              }))
            );
            setIsTableLoading(false);
          }
        );
      })
      .catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
  };
  

  const onRuleNameInputChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    setRuleNameInput(event.target.value);
  };

  const onSelectedRulePredecessorEntity = (value: string) => {
    setSelectedRulePredecessorEntity(value);
  };

  const onIsDefaultChange = (event: CheckboxChangeEvent) => {
    setIsDefault(event.target.checked);
  };

  const onRuleDescriptionChange: React.ChangeEventHandler<
    HTMLTextAreaElement
  > = (event) => {
    setRuleDescriptionInput(event.target.value);
  };

  const onRuleJoinChange: React.ChangeEventHandler<HTMLTextAreaElement> = (
    event
  ) => {
    if (event.target.value.length > 1500) {
      setShowJoinLengthValidationError(true);
      return;
    }
    setShowJoinLengthValidationError(false);
    setJoinInput(event.target.value);
  };

  const onRuleValueChange: React.ChangeEventHandler<HTMLTextAreaElement> = (
    event
  ) => {
    setValueInput(event.target.value);
  };

  const onSaveRule = () => {
    setLoadingButton(true);
    if (editedRow?.id) {
      const model: UpdateMigrationRule = {
        masterId: editedRow?.id,
        tableId: editedRow?.tableId,
        ruleId: editedRow?.ruleId,
        ruleName: ruleNameInput,
        ruleDescription: ruleDescriptionInput,
        joinCondition: joinInput,
        valueColumn: valueInput,
        skipValidation: true,
        defaultRule: isDefault,
        predecessorEntityId: selectedRulePredecessorEntity,
      };
      MigrationRuleService.update(model)
        .then((response: any) => {
          setIsTableLoading(true);  
          onClose();
          NotificationsUtil.openNotificationDialog({
            description: t(
              "Page.Operation.MigrationRules.Tabs.RuleValues.Notifications.Rule.UpdateSuccessDescription",
              {
                lngs: i18n.languages,
                defaultValue: "Rule edited successfully.",
              }
            )!,
            type: NOTIFICATION_TYPES.SUCCESS,
            logError: false,
          });
          setLoadingButton(false);
          MigrationRuleService.getAll(destinationTable.id).then(
            (response: any) => {
              setData(
                response.data.map((x: any) => ({
                  ...x,
                  key: x.id,
                  defaultRule: x.defaultRule === 1 ? true : false,
                  predecessorEntity: x.predecessorEntityId
                    ? entities.find((y: any) => y.id === x.predecessorEntityId)
                        ?.name
                    : "",
                }))
              );
              setIsTableLoading(false);
            }
          );
        })
        .catch((error: any) => {
          let errorData = error.response.data;
          if (error.code === "ERR_BAD_REQUEST") {
            errorData = error.response.data.title;
            errorData += "\n";
            errorData += JSON.stringify(error.response.data.errors);
          }
          setLoadingButton(false);
          NotificationsUtil.openNotificationDialog({
            description: errorData,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        });
    } else {
      const model: CreateMigrationRule = {
        tableId: destinationTable!.id,
        ruleName: ruleNameInput,
        ruleDescription: ruleDescriptionInput,
        joinCondition: joinInput,
        valueColumn: valueInput,
        skipValidation: true,
        defaultRule: isDefault,
        predecessorEntityId: selectedRulePredecessorEntity,
      };
      MigrationRuleService.create(model)
          .then((response: any) => {
          setIsTableLoading(true);
          onSaveClose();
          NotificationsUtil.openNotificationDialog({
            description: t(
              "Page.Operation.MigrationRules.Tabs.RuleValues.Notifications.Rule.CreateSuccessDescription",
              {
                lngs: i18n.languages,
                defaultValue: "Rule created successfully.",
              }
            )!,
            type: NOTIFICATION_TYPES.SUCCESS,
            logError: false,
          });
          setLoadingButton(false);
          MigrationRuleService.getAll(destinationTable.id).then(
              (response: any) => {
              const newData = response.data.map((x: any) => ({
                ...x,
                key: x.id,
                defaultRule: x.defaultRule === 1 ? true : false,
                predecessorEntity: x.predecessorEntityId
                    ? entities.find((y: any) => y.id === x.predecessorEntityId)
                        ?.name
                    : "",
              }));

              setNewRuleValueModalEnabled(false);
              if (newRuleValueModalEnabled && setSelectedRows) {
                setSelectedRows([newData.sort((a, b) => b.id - a.id)[0]]);
                setData(newData.sort((a, b) => b.id - a.id));
                setIsTableLoading(false);
              } else {
                setData(newData);
                setIsTableLoading(false);
              }
            }
          );
        })
        .catch((error: any) => {
          let errorData = error.response.data;
          if (error.code === "ERR_BAD_REQUEST") {
            errorData = error.response.data.title;
            errorData += "\n";
            errorData += JSON.stringify(error.response.data.errors);
          }
          setLoadingButton(false);
          NotificationsUtil.openNotificationDialog({
            description: errorData,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        });
    }
  };

  useEffect(() => {
    if (newRuleValueModalEnabled) {
      showDrawer(RowAction.Create);
    }
  }, [newRuleValueModalEnabled]);

  useEffect(() => {
    if (
      editedRow &&
      (ruleNameInput ||
        isDefault ||
        ruleDescriptionInput ||
        joinInput ||
        valueInput ||
        selectedRulePredecessorEntity)
    ) {
      const def = data.find((x: any) => x.id === editedRow.id);

      if (
        ruleNameInput !== def?.ruleName ||
        isDefault !== def?.defaultRule ||
        ruleDescriptionInput !== def?.ruleDescription ||
        joinInput !== def?.joinCondition ||
        valueInput !== def?.valueColumn ||
        selectedRulePredecessorEntity !== def?.predecessorEntityId
      ) {
        setUpdateRowAllowed(true);
      } else {
        setUpdateRowAllowed(false);
      }
    } else {
      setUpdateRowAllowed(false);
    }
  }, [
    ruleNameInput,
    isDefault,
    ruleDescriptionInput,
    joinInput,
    valueInput,
    editedRow,
    selectedRulePredecessorEntity,
  ]);

  useEffect(() => {
    setIsTableLoading(true);
    if (selectedEntity) {
      const model: GetTablesRequest = {
        entityId: selectedEntity,
        operationId: 2,
        groupCode: "",
      };
      TablesService.getTables(model).then((response: any) => {
        if (response.data.length < 1) {
          setDestinationTable(null);
        } else {
          const table = response.data[0];
          setDestinationTable(table);
        }
      });
    } else {
      setDestinationTable(null);
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (destinationTable) {
      setIsTableLoading(true);
      MigrationRuleService.getAll(destinationTable.id).then((response: any) => {
        setData(
          response.data.map((x: any) => ({
            ...x,
            key: x.id,
            defaultRule: x.defaultRule === 1 ? true : false,
            predecessorEntity: x.predecessorEntityId
              ? entities.find((y: any) => y.id === x.predecessorEntityId)?.name
              : "",
          }))
        );
        setIsTableLoading(false);
      });
    } else {
      setData([]);
      setIsTableLoading(false);
    }
  }, [destinationTable]);

  const showJoin = (value: string) => {
    return (
      <div>
        {value}
      </div>
    );
  }

  const columns: ColumnsType<DataType> = [
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleValues.Table.Headers.RuleID",
        {
          lngs: i18n.languages,
          defaultValue: "Rule ID",
        }
      ),
      dataIndex: "ruleId",
      key: "ruleId",
      width: "100px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.ruleId > b.ruleId ? 1 : -1,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleValues.Table.Headers.RuleName",
        {
          lngs: i18n.languages,
          defaultValue: "Rule Name",
        }
      ),
      dataIndex: "ruleName",
      key: "ruleName",
      filters: [],
      filterMode: "tree",
      width: "200px",
      filterSearch: true,
      sorter: (a, b) => a.ruleName ? a.ruleName.localeCompare(b.ruleName ? b.ruleName : "") : "".localeCompare(b.ruleName ? b.ruleName : ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleValues.Table.Headers.RuleDescription",
        {
          lngs: i18n.languages,
          defaultValue: "Rule Description",
        }
      ),
      dataIndex: "ruleDescription",
      key: "ruleDescription",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.ruleDescription ? a.ruleDescription.localeCompare(b.ruleDescription ? b.ruleDescription : "") : "".localeCompare(b.ruleDescription ? b.ruleDescription : ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleValues.Table.Headers.PredecessorEntity",
        {
          lngs: i18n.languages,
          defaultValue: "Predecessor Entity",
        }
      ),
      dataIndex: "predecessorEntity",
      key: "predecessorEntity",
      filters: [],
      filterMode: "tree",
      width: "200px",
      filterSearch: true,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleValues.Table.Headers.Join",
        {
          lngs: i18n.languages,
          defaultValue: "Join",
        }
      ),
      dataIndex: "joinCondition",
      key: "joinCondition",
      filters: [],
      filterMode: "tree",
      width: "100px",
      filterSearch: true,
      sorter: (a, b) => a.joinCondition ? a.joinCondition.localeCompare(b.joinCondition ? b.joinCondition : "") : "".localeCompare(b.joinCondition ? b.joinCondition : ""),
      render: (val, rec) => (
        <Popover content={() => showJoin(val)} title="Join condition">
          <div>
            {val.length > 80
              ? val.slice(0, 80) + " ..."
              : val}
          </div>
        </Popover>
      ),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleValues.Table.Headers.Value",
        {
          lngs: i18n.languages,
          defaultValue: "Value",
        }
      ),
      dataIndex: "valueColumn",
      key: "valueColumn",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.valueColumn ? a.valueColumn.localeCompare(b.valueColumn ? b.valueColumn : "") : "".localeCompare(b.valueColumn ? b.valueColumn : ""),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleValues.Table.Headers.Default",
        {
          lngs: i18n.languages,
          defaultValue: "Default",
        }
      ),
      key: "defaultRule",
      dataIndex: "defaultRule",
      filters: [],
      width: "100px",
      align: "center",
      filterMode: "tree",
      filterSearch: true,
    },
  ];

  const scroll = { y: selectable ? "calc(-270px + 91vh)" : "calc(100vh - 330px)", x: "max-content" };

  return (
    <>
      {!selectedEntity ? (
        <Empty
          style={{ display: "flex", width: "100%" }}
          description={t(
            "Page.Operation.MigrationRules.Tabs.RuleValues.Tutorial.GetStarted",
            {
              lngs: i18n.languages,
              defaultValue: "Please select an Entity to get started",
            }
          )}
        ></Empty>
      ) : (
        <Datatable
          scroll={scroll}
          tableColumns={columns}
          data={data}
          setData={setData}
          isTableLoading={isTableLoading}
          otherActions={[]}
          otherRowEnabled={false}
          editRowEnabled={true}
          copyRowEnabled={true}
          deleteRowEnabled={true}
          onEditFunc={onEditRule}
          onCopyFunc={onCopyRule}
          onDeleteFunc={onDeleteRule}
          onDoubleClickRow={onDoubleClickRow}
          selectable={selectable}
          multiSelect={multiSelect}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          checkPermissions={true}
          defaultSortColumn={sortColumn}
          defaultSortColumnDirection={"descend"}
          paginationDisabled={true}
        ></Datatable>
      )}
      <Drawer
        width={"400px"}
        getContainer={false}
        style={{ position: "absolute", display: openDrawer ? "block" : "none" }}
        title={drawerTitle}
        placement={DRAWER_PLACEMENTS.BOTTOM}
        onClose={onClose}
        visible={openDrawer}
        closable={false}
        height={"550px"}
        extra={
          <Space>
            {RowAction?.View === drawerAction && (
              <>
                <Button
                  onClick={() => showDrawer(RowAction.Edit)}
                  icon={<EditOutlined />}
                  className={"active-btn"}
                  size="small"
                >
                  {t("General.Labels.Edit", {
                    lngs: i18n.languages,
                    defaultValue: "Edit",
                  })}
                </Button>
                <Button
                  type="ghost"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  size="small"
                />
              </>
            )}
            {RowAction?.Create === drawerAction && (
              <>
                <Button
                  disabled={!valueInput}
                  className={!valueInput ? "" : "active-btn"}
                  onClick={onSaveRule}
                  icon={<CheckOutlined />}
                  size="small"
                  loading={loadingButton}
                >
                  {t("General.Labels.Save", {
                    lngs: i18n.languages,
                    defaultValue: "Save",
                  })}
                </Button>
                <Button
                  type="ghost"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  size="small"
                />
              </>
            )}
            {RowAction?.Edit === drawerAction && (
              <>
                <Button
                  disabled={!updateRowAllowed}
                  className={!updateRowAllowed ? "" : "active-btn"}
                  onClick={onSaveRule}
                  icon={<CheckOutlined />}
                  size="small"
                  loading={loadingButton}
                >
                  {t("General.Labels.Update", {
                    lngs: i18n.languages,
                    defaultValue: "Update",
                  })}
                </Button>
                <Button
                  type="ghost"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  size="small"
                />
              </>
            )}
          </Space>
        }
      >
        <Form
          disabled={RowAction.View === drawerAction ? true : false}
          layout="vertical"
        >
          <div className="form-top">
            <Form.Item
              label={t(
                "Page.Operation.MigrationRules.Tabs.RuleValues.Labels.RuleName",
                {
                  lngs: i18n.languages,
                  defaultValue: "Rule Name",
                }
              )}
              tooltip={t(
                "Page.Operation.MigrationRules.Tabs.RuleValues.Tooltips.RuleName",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
              required={true}
            >
              <Input
                allowClear
                value={ruleNameInput}
                placeholder={
                  t("General.Placeholders.EnterValue", {
                    lngs: i18n.languages,
                    defaultValue: "Enter value",
                  })!
                }
                onChange={onRuleNameInputChange}
              ></Input>
            </Form.Item>
            <Form.Item
              label={t(
                "Page.Operation.MigrationRules.Tabs.RuleValues.Labels.IsDefault",
                {
                  lngs: i18n.languages,
                  defaultValue: "Is Default",
                }
              )}
              tooltip={t(
                "Page.Operation.MigrationRules.Tabs.RuleValues.Tooltips.IsDefault",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
            >
              <Checkbox checked={isDefault} onChange={onIsDefaultChange}>
                {t(
                  "Page.Operation.MigrationRules.Tabs.RuleValues.Labels.IsDefault",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Is Default",
                  }
                )}
              </Checkbox>
            </Form.Item>
            <Form.Item
              style={{ width: "200px" }}
              label={t(
                "Page.Operation.MigrationRules.Tabs.RuleValues.Labels.PredecessorEntity",
                {
                  lngs: i18n.languages,
                  defaultValue: "Predecessor Entity",
                }
              )}
              tooltip={t(
                "Page.Operation.MigrationRules.Tabs.RuleValues.Tooltips.PredecessorEntity",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
            >
              <Select
                placeholder={t("General.Placeholders.SelectOption", {
                  lngs: i18n.languages,
                  defaultValue: "Select option",
                })}
                allowClear
                showSearch={true}
                showArrow={true}
                value={selectedRulePredecessorEntity}
                onChange={onSelectedRulePredecessorEntity}
                options={entities
                  .filter((x:any) => x.id !== selectedEntity)
                  .map((x: any) => ({
                    label: x.name,
                    value: x.id,
                }))}
                filterSort={(optionA, optionB) =>
                    (optionA!.label as unknown as string)
                        .toLowerCase()
                        .localeCompare(
                            (optionB!.label as unknown as string).toLowerCase()
                        )
                }
                filterOption={(inputValue: any, option: any) =>
                  option!.label
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
              ></Select>
            </Form.Item>
          </div>
          <Form.Item
            label={t(
              "Page.Operation.MigrationRules.Tabs.RuleValues.Labels.RuleDescription",
              {
                lngs: i18n.languages,
                defaultValue: "Rule Description",
              }
            )}
            tooltip={t(
              "Page.Operation.MigrationRules.Tabs.RuleValues.Tooltips.RuleDescription",
              {
                lngs: i18n.languages,
                defaultValue: "",
              }
            )}
          >
            <Input.TextArea
              allowClear
              rows={4}
              value={ruleDescriptionInput}
              placeholder={
                t("General.Placeholders.EnterValue", {
                  lngs: i18n.languages,
                  defaultValue: "Enter value",
                })!
              }
              onChange={onRuleDescriptionChange}
            ></Input.TextArea>
          </Form.Item>
          <Form.Item
            label={t(
              "Page.Operation.MigrationRules.Tabs.RuleValues.Labels.Join",
              {
                lngs: i18n.languages,
                defaultValue: "Join",
              }
            )}
            tooltip={t(
              "Page.Operation.MigrationRules.Tabs.RuleValues.Tooltips.Join",
              {
                lngs: i18n.languages,
                defaultValue: "",
              }
            )}
          >
            <Input.TextArea
              allowClear
              rows={4}
              value={joinInput}
              placeholder={
                t("General.Placeholders.EnterValue", {
                  lngs: i18n.languages,
                  defaultValue: "Enter value",
                })!
              }
              onChange={onRuleJoinChange}
            ></Input.TextArea>
            {showJoinLengthValidationError && (
              <span style={{color: "red"}}>Maximum amount of characters is 1500.</span>
            )}
          </Form.Item>
          <Form.Item
            label={t(
              "Page.Operation.MigrationRules.Tabs.RuleValues.Labels.Value",
              {
                lngs: i18n.languages,
                defaultValue: "Value",
              }
            )}
            tooltip={t(
              "Page.Operation.MigrationRules.Tabs.RuleValues.Tooltips.Value",
              {
                lngs: i18n.languages,
                defaultValue: "",
              }
            )}
            required={true}
          >
            <Input.TextArea
              allowClear
              rows={4}
              value={valueInput}
              placeholder={
                t("General.Placeholders.EnterValue", {
                  lngs: i18n.languages,
                  defaultValue: "Enter value",
                })!
              }
              onChange={onRuleValueChange}
            ></Input.TextArea>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default RuleValues;
